import { useEffect, useState } from "react";

// 30-min slots from 07:00 to 22:00
export const START_HOUR = 7;
export const END_HOUR = 22;
export const SLOT_MIN = 30;
export const SLOT_PX = 30;                       // height of one 30-min slot (px)
export const SLOTS_PER_HOUR = 60 / SLOT_MIN;
export const TOTAL_SLOTS = (END_HOUR - START_HOUR) * SLOTS_PER_HOUR;
export const SLOTS = Array.from({ length: TOTAL_SLOTS }, (_, i) => ({
  index: i,
  hour: START_HOUR + Math.floor(i / SLOTS_PER_HOUR),
  minute: (i % SLOTS_PER_HOUR) * SLOT_MIN,
}));

// ---- date utils ----
export function ymd(d) { return d.toISOString().slice(0, 10); }
export function isoAt(dateStr, hour, minute = 0) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, hour, minute)).toISOString();
}
export function startOfWeek(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const dow = d.getUTCDay();
  const off = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + off);
  return ymd(d);
}
export function addDays(dateStr, delta) {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + delta);
  return ymd(d);
}
export function startOfMonth(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z"); d.setUTCDate(1); return ymd(d);
}
export function jsDowToKey(dow) { return String(dow === 0 ? 6 : dow - 1); }
export function timeToMin(t) { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; }
export function slotInRange(dateStr, hour, minute, hoursCfg) {
  if (!hoursCfg) return true;
  const dow = new Date(dateStr + "T00:00:00Z").getUTCDay();
  const cfg = hoursCfg[jsDowToKey(dow)];
  if (!cfg || cfg.closed) return false;
  const t = hour * 60 + minute;
  const morning = cfg.open && cfg.close && timeToMin(cfg.open) <= t && t < timeToMin(cfg.close);
  const afternoon = cfg.open2 && cfg.close2 && timeToMin(cfg.open2) <= t && t < timeToMin(cfg.close2);
  return morning || afternoon;
}

// Helper: check if (hour,minute) falls inside a dayCfg range object
export function slotInCfgRange(cfg, hour, minute) {
  if (!cfg || cfg.closed) return false;
  const t = hour * 60 + minute;
  const p = (hhmm) => { if (!hhmm) return null; const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
  const s1 = p(cfg.open), e1 = p(cfg.close);
  if (s1 != null && e1 != null && s1 <= t && t < e1) return true;
  const s2 = p(cfg.open2), e2 = p(cfg.close2);
  if (s2 != null && e2 != null && s2 <= t && t < e2) return true;
  return false;
}

// Default opening hours used when the tenant hasn't configured its own.
// Same shape as tenant.opening_hours: keys "0"..."6" where 0=Monday.
export const DEFAULT_OPEN_HOURS = {
  "0": { open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" },
  "1": { open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" },
  "2": { open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" },
  "3": { open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" },
  "4": { open: "09:00", close: "13:00", open2: "15:00", close2: "19:00" },
  "5": { open: "09:00", close: "18:00" },
  "6": { closed: true },
};

export const DAY_LABELS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
export const MONTH_LABELS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// ---- overlap layout ----
// For a list of appointments (same column/day), compute {col, cols} for each so
// overlapping chips are shown side-by-side.
export function computeOverlapLayout(list) {
  const sorted = [...list].sort((a, b) => new Date(a.start) - new Date(b.start));
  const layout = new Map(); // apptId -> { col, cols }
  let cluster = [];
  let clusterEnd = 0;
  const flushCluster = () => {
    if (cluster.length === 0) return;
    // Greedy column assignment
    const cols = []; // cols[i] = endMs of last appt in that column
    const assigned = new Map();
    for (const a of cluster) {
      const s = new Date(a.start).getTime();
      const e = new Date(a.end).getTime();
      let placed = -1;
      for (let i = 0; i < cols.length; i++) {
        if (cols[i] <= s) { placed = i; break; }
      }
      if (placed === -1) { cols.push(e); placed = cols.length - 1; }
      else cols[placed] = e;
      assigned.set(a.id, placed);
    }
    const total = cols.length;
    for (const a of cluster) layout.set(a.id, { col: assigned.get(a.id), cols: total });
    cluster = [];
    clusterEnd = 0;
  };
  for (const a of sorted) {
    const s = new Date(a.start).getTime();
    const e = new Date(a.end).getTime();
    if (cluster.length === 0 || s < clusterEnd) {
      cluster.push(a);
      clusterEnd = Math.max(clusterEnd, e);
    } else {
      flushCluster();
      cluster.push(a);
      clusterEnd = e;
    }
  }
  flushCluster();
  return layout;
}

// ---- Now-line hook: returns { minutesFromMidnight } that ticks each minute
export function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}
